import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../common/Button';
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Logo from '../Logo';
import { useUnreadCount } from '../../../features/chat/hooks';

const Header = ({ gebruiker, onLogout }) => {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { totalUnread } = useUnreadCount();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setScrolled(window.scrollY > 20);
  }, [location.pathname]);

  useEffect(() => {
    setHasAnimated(true);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowProfileDropdown(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = [
    { name: 'Homepagina', href: '/', isActive: location.pathname === '/' },
    { name: 'Blogs', href: '/blogs', isActive: location.pathname === '/blogs' },
    { name: 'Contact', href: '/contact', isActive: location.pathname === '/contact' },
  ];

  const profileDropdownItems = [
    { name: 'Ga naar Dashboard', action: () => navigate('/dashboard'), icon: Cog6ToothIcon, color: 'text-blue-600', bgColor: 'hover:bg-blue-50' },
    { name: 'Berichten', action: () => navigate('/dashboard', { state: { originSection: 'berichten' } }), icon: ChatBubbleLeftRightIcon, color: 'text-green-600', bgColor: 'hover:bg-green-50' },
    {
      name: 'Uitloggen',
      action: () => { onLogout(); setShowProfileDropdown(false); },
      icon: ArrowRightOnRectangleIcon,
      color: 'text-red-600',
      bgColor: 'hover:bg-red-50',
    },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[45] transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200/50'
          : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div
            className="flex-shrink-0 cursor-pointer group"
            onClick={() => navigate('/')}
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="transition-transform duration-300 group-hover:scale-105">
                <Logo size={32} />
              </div>
              <div className="leading-tight">
                <h1 className="text-lg sm:text-xl md:text-2xl font-extrabold tracking-tight text-indigo-700">
                  Studiebijbaan.nl
                </h1>
                <p className="hidden sm:block text-[11px] uppercase tracking-[0.18em] text-gray-500">Kansen voor studenten</p>
              </div>
            </div>
          </div>

          {/* Navigation Links - Desktop */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.href}
                  className={`group relative px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center ${
                    item.isActive
                      ? 'text-indigo-700 bg-indigo-50'
                      : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{item.name}</span>
                </Link>
              </div>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {gebruiker ? (
              <>
                {/* Dashboard Button - desktop only */}
                <div className="hidden sm:block">
                  <Button
                    variant="orange"
                    size="sm"
                    onClick={() => navigate('/dashboard')}
                  >
                    <div className="flex items-center space-x-2">
                      <Cog6ToothIcon className="w-5 h-5" />
                      <span>Dashboard</span>
                    </div>
                  </Button>
                </div>

                {/* Chat Notifications */}
                <button
                  onClick={() => navigate('/dashboard', { state: { originSection: 'berichten' } })}
                  className="relative p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
                  title="Berichten"
                >
                  <ChatBubbleLeftRightIcon className="w-6 h-6" />
                  {totalUnread > 0 && (
                    <div className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 rounded-full flex items-center justify-center px-1.5 shadow-sm">
                      <span className="text-xs font-bold text-white">
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    </div>
                  )}
                </button>

                {/* Profile Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center space-x-2 p-2 rounded-xl hover:bg-gray-50 transition-colors group"
                  >
                    <div className="relative">
                      <div
                        className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold shadow-sm transition-transform duration-200 group-hover:scale-105"
                      >
                        {gebruiker.naam?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div
                        className={`absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full border-2 border-gray-200 flex items-center justify-center transition-transform duration-200 ${showProfileDropdown ? 'rotate-180' : ''}`}
                      >
                        <ChevronDownIcon className="w-3 h-3 text-gray-600" />
                      </div>
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-gray-900">{gebruiker.naam}</p>
                      <p className="text-xs text-gray-500">{gebruiker.email}</p>
                    </div>
                  </button>

                  <AnimatePresence>
                    {showProfileDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 sm:w-64 bg-white rounded-xl shadow-lg border border-gray-200/50 backdrop-blur-md overflow-hidden"
                      >
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm shrink-0">
                              {gebruiker.naam?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{gebruiker.naam}</p>
                              <p className="text-xs text-gray-600 truncate">{gebruiker.email}</p>
                              {gebruiker.studentNumber && (
                                <p className="text-xs font-mono font-medium text-indigo-600 mt-1">ID: {gebruiker.studentNumber}</p>
                              )}
                              {gebruiker.employerNumber && (
                                <p className="text-xs font-mono font-medium text-emerald-600 mt-1">ID: {gebruiker.employerNumber}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="py-2">
                          {profileDropdownItems.map((item) => (
                            <button
                              key={item.name}
                              onClick={item.action}
                              className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors group ${item.bgColor} ${item.color}`}
                            >
                              <item.icon className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                              <span className="text-sm font-medium">{item.name}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile hamburger — md:hidden */}
                <div className="relative md:hidden" ref={mobileMenuRef}>
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'}
                  >
                    {mobileMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                  </button>

                  <AnimatePresence>
                    {mobileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
                      >
                        <div className="py-2">
                          {navigationItems.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`flex items-center px-4 py-3 text-sm font-medium transition-colors ${
                                item.isActive
                                  ? 'text-indigo-600 bg-indigo-50'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span>{item.name}</span>
                            </Link>
                          ))}
                          <div className="border-t border-gray-100 my-1" />
                          <Link
                            to="/dashboard"
                            className="flex items-center space-x-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Cog6ToothIcon className="w-5 h-5" />
                            <span>Dashboard</span>
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              // Not logged in state
              <>
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <button
                    onClick={() => navigate('/inloggen')}
                    className="text-sm font-semibold text-gray-600 hover:text-indigo-600 px-1 transition-colors"
                  >
                    Inloggen
                  </button>
                  <Button
                    variant="orange"
                    size="md"
                    onClick={() => navigate('/registreren')}
                    className="whitespace-nowrap shadow-md font-bold px-5 py-2.5 sm:px-6"
                  >
                    Registreren
                  </Button>
                </div>

                {/* Mobile hamburger for logged-out users */}
                <div className="relative md:hidden" ref={mobileMenuRef}>
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="ml-1 p-2 text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors border border-gray-200/50"
                    aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'}
                  >
                    {mobileMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6 text-indigo-700" />}
                  </button>

                  <AnimatePresence>
                    {mobileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
                      >
                        <div className="py-2">
                          {navigationItems.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`flex items-center px-4 py-3 text-sm font-medium transition-colors ${
                                item.isActive
                                  ? 'text-indigo-600 bg-indigo-50'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span>{item.name}</span>
                            </Link>
                          ))}
                          <div className="border-t border-gray-100 my-1" />
                          <Link
                            to="/inloggen"
                            className="flex items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            Inloggen
                          </Link>
                          <Link
                            to="/registreren"
                            className="flex items-center px-4 py-3 text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors font-bold"
                          >
                            Registreren
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default React.memo(Header);
